import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DealMachinePanel } from "@/components/DealMachinePanel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DualScore from "@/components/DualScore";
import EvidenceBadge from "@/components/EvidenceBadge";
import { toast } from "sonner";
import { FileText, Calculator, ImagePlus, Trash2, ImageIcon, MapPin, ExternalLink, Zap, Timer } from "lucide-react";
import CriticalFactsEditor from "@/components/CriticalFactsEditor";

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = (opts = {}) => {
    api.get(`/properties/${id}`).then(r => setP(r.data)).catch(() => {});
    if (!opts.skipMatch) {
      setMatchesLoading(true);
      api.get(`/match/property/${id}`)
        .then(r => setMatches(r.data.matches || []))
        .catch(() => setMatches([]))
        .finally(() => setMatchesLoading(false));
    }
  };
  useEffect(() => { load(); }, [id]);

  const generatePacket = async (programId = "") => {
    const { data } = await api.post("/packets", { property_id: id, program_id: programId || "", packet_type: "conceptual" });
    toast.success("Conceptual packet created");
    navigate(`/packets/${data.id}`);
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/properties/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length>1?"s":""} uploaded`);
      load({ skipMatch: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    if (!confirm("Delete this photo?")) return;
    await api.delete(`/properties/${id}/photos/${photoId}`);
    load({ skipMatch: true });
  };

  if (!p) return <div>Loading…</div>;
  const token = localStorage.getItem("sahoi_token") || "";
  const photoUrl = (ph) => `${API}/files/${ph.storage_path}?auth=${encodeURIComponent(token)}`;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/properties" className="text-xs text-slate-500 hover:text-slate-900">← All Properties</Link>
          <div className="flex items-start justify-between gap-4 mt-2">
            <div>
              <h1 className="text-3xl font-bold" data-testid="property-title">{p.address}</h1>
              <div className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{p.city}, TX {p.zip_code} · {p.county} County</div>
            </div>
            <div className="flex gap-2 shrink-0">
          <Button
            onClick={() => generatePacket(matches[0]?.program?.id || "")}
            data-testid="generate-packet-btn"
            className="bg-[#C25E00] hover:bg-[#a34f00] text-xs font-semibold uppercase tracking-wider text-white shrink-0"
          >
            <FileText className="w-3.5 h-3.5 mr-1" /> Generate packet
          </Button>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${p.address}, ${p.city}, TX${p.zip_code ? " " + p.zip_code : ""}`.trim())}`}
            target="_blank"
            rel="noreferrer"
            data-testid="open-in-maps-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-900 bg-slate-900 text-xs font-semibold uppercase tracking-wider text-white hover:bg-slate-800 transition-colors shrink-0"
          >
            <MapPin className="w-3.5 h-3.5" /> Google Maps <ExternalLink className="w-3 h-3 opacity-70" />
          </a>
          <a
            href={`https://www.zillow.com/homes/${encodeURIComponent(`${p.address}, ${p.city}, TX${p.zip_code ? " " + p.zip_code : ""}`.trim())}_rb/`}
            target="_blank"
            rel="noreferrer"
            data-testid="open-in-zillow-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#006AFF] bg-[#006AFF] text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#0056cc] transition-colors shrink-0"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Zillow Profile
          </a>
            </div>
          </div>
      </div>

      <CriticalFactsEditor propertyId={id} onChanged={() => load({ skipMatch: true })} />

      {user?.role === "admin" && <DealMachinePanel property={p} onChange={() => load({ skipMatch: true })} />}

      <Card className="p-5 border-slate-200" data-testid="photos-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-900 font-semibold"><ImageIcon className="w-4 h-4" /> Photos ({(p.photos||[]).length})</div>
          <div>
            <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={uploadPhoto} data-testid="photo-file-input" />
            <Button size="sm" onClick={()=>fileInput.current?.click()} disabled={uploading}
                    className="bg-slate-900 hover:bg-slate-800" data-testid="upload-photo-btn">
              <ImagePlus className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : "Upload photo"}
            </Button>
          </div>
        </div>
        {(p.photos||[]).length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
            No photos yet. Upload interior, exterior, or condition photos to include in the Info Packet.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {p.photos.map(ph => (
              <div key={ph.id} className="relative group aspect-video rounded-md overflow-hidden bg-slate-100 border border-slate-200" data-testid={`photo-${ph.id}`}>
                <img src={photoUrl(ph)} alt={ph.filename} className="w-full h-full object-cover" />
                <button onClick={()=>removePhoto(ph.id)}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-white/95 rounded-md p-1.5 shadow hover:bg-rose-50"
                        data-testid={`delete-photo-${ph.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 text-[10px] text-white truncate">
                  {ph.filename}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid md:grid-cols-3 gap-5">
        <Card className="p-5 md:col-span-2 border-slate-200">
          <h3 className="text-base font-semibold mb-3">Property Details</h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Info label="Type" v={p.property_type} />
            <Info label="Beds" v={p.bedrooms} />
            <Info label="Baths" v={p.bathrooms} />
            <Info label="Sqft" v={p.sqft} />
            <Info label="Year Built" v={p.year_built} />
            <Info label="Zoning" v={p.zoning} />
            <Info label="Accessibility" v={p.accessibility} />
            <Info label="Parking" v={p.parking} />
          </div>
        </Card>
        <Card className="p-5 border-slate-200 space-y-3">
          <h3 className="text-base font-semibold">Economics</h3>
          <Info label="Acquisition" v={`$${Number(p.acquisition_cost||0).toLocaleString()}`} />
          <Info label="Conversion" v={`$${Number(p.conversion_cost||0).toLocaleString()}`} />
          <Info label="Conventional Rent/mo" v={`$${Number(p.monthly_conventional_rent||0).toLocaleString()}`} />
          <Info label="Specialized Rent/mo" v={`$${Number(p.monthly_specialized_rent||0).toLocaleString()}`} className="text-[#C25E00] font-semibold" />
          <Button onClick={()=>navigate(`/underwriting?property=${id}`)} variant="outline" className="w-full" data-testid="uw-btn">
            <Calculator className="w-4 h-4 mr-1"/> Underwrite
          </Button>
        </Card>
      </div>

      {matches.length > 0 && (
        <div data-testid="compare-alternatives">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Decision Aid</div>
              <h2 className="text-xl font-semibold">Compare Alternatives — Top 3 Pathways</h2>
            </div>
            <div className="text-xs text-slate-500">Best-yield specialized pathways ranked by Opportunity Score</div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {matches.slice(0, 3).map((m, idx) => {
              const prog = m.program;
              const wait = (prog.waitlist_size || "").toLowerCase();
              let velocity;
              if (wait.includes("no wait") || wait.includes("no open") || wait.includes("same-day")) velocity = { label: "Fast", cls: "bg-emerald-100 text-emerald-800" };
              else if (wait.includes("10+") || wait.includes("10 years") || wait.includes("200,000")) velocity = { label: "Slow", cls: "bg-rose-100 text-rose-800" };
              else if (wait) velocity = { label: "Moderate", cls: "bg-amber-100 text-amber-800" };
              else velocity = { label: "Unknown", cls: "bg-slate-100 text-slate-600" };
              return (
                <Card key={prog.id} className={`p-4 border-slate-200 ${idx === 0 ? "ring-2 ring-[#C25E00] border-[#C25E00]" : ""}`} data-testid={`compare-card-${prog.id}`}>
                  {idx === 0 && <div className="text-[10px] uppercase tracking-widest text-[#C25E00] font-bold mb-2">Recommended</div>}
                  <Link to={`/programs/${prog.id}`} className="font-semibold text-slate-900 leading-tight hover:text-[#C25E00] block">{prog.name}</Link>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{prog.vertical?.replace("_", " ")}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-900 text-white font-semibold uppercase tracking-wider">
                      {(prog.rent_basis || "per_unit").replace("_", " ")}
                    </span>
                  </div>
                  <div className="mt-3 space-y-2 text-xs">
                    <CmpRow k="Est. Monthly Gross" v={<span className="font-mono font-bold text-[#C25E00]">${Number(p.monthly_specialized_rent || 0).toLocaleString()}</span>} />
                    <CmpRow k="Admin Partner" v={<span className="text-slate-800">{prog.admin_agent || prog.agency || "—"}</span>} />
                    <CmpRow k="Referral Velocity" v={<span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${velocity.cls}`} data-testid={`velocity-${prog.id}`}>{velocity.label}</span>} />
                    <CmpRow k="Waitlist" v={<span className="text-slate-700">{prog.waitlist_size || "—"}</span>} />
                    <CmpRow k="Avg Stay" v={<span className="text-slate-700">{prog.avg_length_of_stay || "—"}</span>} />
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <DualScore opportunity={m.opportunity_score} confidence={m.confidence_score} />
                  </div>
                  <Button size="sm" onClick={() => generatePacket(prog.id)} className="w-full mt-3 bg-slate-900 hover:bg-slate-800" data-testid={`compare-packet-${prog.id}`}>
                    <FileText className="w-3.5 h-3.5 mr-1"/> Generate Packet
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      )}


      <div>
        <h2 className="text-xl font-semibold mb-3">Matching Housing Programs</h2>
        <div className="space-y-3">
          {matches.slice(0, 8).map(m => (            <Card key={m.program.id} className="p-4 border-slate-200">
              <div className="flex justify-between items-start gap-6">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Link to={`/programs/${m.program.id}`} className="font-semibold hover:text-[#C25E00]">{m.program.name}</Link>
                    <EvidenceBadge level={m.program.evidence_level} />
                    {m.program.rent_basis && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-900 text-white font-semibold uppercase tracking-wider" data-testid={`rent-basis-chip-${m.program.id}`}>
                        {m.program.rent_basis.replace("_", " ")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 capitalize">{m.program.vertical?.replace("_"," ")} · {m.program.agency}</div>
                  <ul className="text-xs text-emerald-700 mt-2 list-disc list-inside space-y-0.5">
                    {(m.reasons||[]).map((r,i)=><li key={i}>{r}</li>)}
                  </ul>
                  {(m.penalties||[]).length > 0 && <ul className="text-xs text-rose-600 mt-1 list-disc list-inside">{m.penalties.map((r,i)=><li key={i}>{r}</li>)}</ul>}
                </div>
                <div className="w-64 shrink-0 space-y-2">
                  <DualScore opportunity={m.opportunity_score} confidence={m.confidence_score} />
                  <Button size="sm" variant="outline" className="w-full" onClick={()=>generatePacket(m.program.id)} data-testid={`packet-btn-${m.program.id}`}>
                    <FileText className="w-3.5 h-3.5 mr-1" /> Generate Packet
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {!matchesLoading && matches.length === 0 && <div className="text-sm text-slate-500">No matching programs yet.</div>}
          {matchesLoading && <div className="text-sm text-slate-400">Loading matches…</div>}
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, v, className = "" }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className={`text-sm ${className || "text-slate-800"}`}>{v || <span className="text-slate-400">—</span>}</div>
  </div>
);

const CmpRow = ({ k, v }) => (
  <div className="grid grid-cols-5 gap-2">
    <div className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
    <div className="col-span-3 truncate">{v}</div>
  </div>
);
